import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCoverageOptions,
  getPendingCoverage,
  getProjectTimeOffCoverage,
  saveSubstitutes,
  type CoverageOptions,
  type ProjectCoverageEntry,
  type StoredSubstitute,
  type SubstituteSelection,
  type TimeOffCoverage,
} from '../services/api/timeOffCoverage';

/**
 * MKS-361 — TanStack bindings for time-off substitutes.
 *
 * Each hook takes an `enabled` switch rather than being called conditionally,
 * because every caller gates on the `mks_361_time_off_substitutes` flag and
 * `useQuery` cannot live behind an `if`.
 */

/**
 * The request form's payload. Keyed on the dates (or the request id), so
 * moving the date range refetches rather than showing yesterday's projects.
 */
export function useCoverageOptions(
  params: { dateFrom?: string; dateTo?: string; timeOffTypeId?: number; timeOffId?: number },
  enabled: boolean,
) {
  const hasRange = Boolean(params.dateFrom && params.dateTo);
  return useQuery<CoverageOptions>({
    queryKey: [
      'timeOffCoverageOptions',
      params.timeOffId ?? null,
      params.dateFrom ?? null,
      params.dateTo ?? null,
      params.timeOffTypeId ?? null,
    ],
    queryFn: () => getCoverageOptions(params),
    enabled: enabled && (hasRange || params.timeOffId !== undefined),
  });
}

/** Substitutes and overlap warnings for everything in the approval queue. */
export function usePendingCoverage(enabled: boolean) {
  return useQuery<TimeOffCoverage[]>({
    queryKey: ['pendingTimeOffCoverage'],
    queryFn: getPendingCoverage,
    enabled,
  });
}

/** The PM panel's "who is out between these dates, and who covers". */
export function useProjectTimeOffCoverage(
  projectId: number | undefined,
  from: string,
  to: string,
  enabled: boolean,
) {
  return useQuery<ProjectCoverageEntry[]>({
    queryKey: ['projectTimeOffCoverage', projectId, from, to],
    queryFn: () => getProjectTimeOffCoverage(projectId!, from, to),
    enabled: enabled && projectId !== undefined,
  });
}

/**
 * Replace the substitutes on one request — the approver's "change the
 * substitute". Invalidates the approval queue's coverage and the options
 * query, so the row and any open editor both redraw from the server.
 */
export function useSaveSubstitutes() {
  const queryClient = useQueryClient();
  return useMutation<
    StoredSubstitute[],
    Error,
    { timeOffId: number; substitutes: SubstituteSelection[] }
  >({
    mutationFn: ({ timeOffId, substitutes }) => saveSubstitutes(timeOffId, substitutes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pendingTimeOffCoverage'] });
      void queryClient.invalidateQueries({ queryKey: ['timeOffCoverageOptions'] });
      void queryClient.invalidateQueries({ queryKey: ['projectTimeOffCoverage'] });
    },
  });
}
